import React, { useEffect, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import ApproveTable from "../../components/Table/ApproveTable";
import {
  formatIsoDate,
  renderStatus,
  formatName,
  formatPositionLabel,
  toUiPosition,
  isProgramChairPositionName,
} from "../../utils/generalUtils";
import GeneratePdf from "../../generatePdf/GeneratePdf";
import { Select, Space, Tag } from "antd";
import TableSkeleton from "../../components/Loading/TableSkeleton";

const ApproveCos = () => {
  const { response, loading, fetchData } = useAxios();
  const { approverCosPopup } = useSelector((state) => state.generalSlice);
  const { id: employee_id, positions } = useSelector(
    (state) => state.accountDetailSlice.details
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/cos/${positionId}`, method: "GET" });
  };

  const mappedEmpPos = positions
    .filter((item) => item?.is_approver && !isProgramChairPositionName(item?.position_name))
    .map((item) => {
      const normalized = toUiPosition(item);

      return {
        value: normalized?.position_id,
        label: formatPositionLabel(normalized),
        disabled: normalized?.is_inactive,
      };
    });
  const activeEmpPos = mappedEmpPos.filter((item) => !item.disabled);
  const hasActiveApproverPosition = activeEmpPos.length > 0;

  const [selectedPos, setSelectedPos] = useState(
    activeEmpPos.length > 0 ? activeEmpPos[0].value : undefined
  );

  useEffect(() => {
    if (
      (selectedPos === undefined || selectedPos === null) &&
      activeEmpPos.length > 0
    ) {
      setSelectedPos(activeEmpPos[0].value);
    }
  }, [activeEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) {
      return;
    }

    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      submitter_position: item?.submission?.position?.position_name,
      submitter_position_unit: item?.submission?.position?.unit,
      ...item?.submission?.certificate_of_service,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  //columns for table
  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Position",
      dataIndex: "submitter_position",
      key: "submitter_position",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
        ["cancelled"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return (
          <GeneratePdf
            docuType="cos"
            contents={item}
            disabled={item?.status !== "approved"}
          />
        );
      },
    },
  ];

  //this is required for the forms (this is not dummy data)
  const cosfields = [
    {
      name: "submission_id",
      label: "submission id",
      hidden: true,
      disabled: true,
    },
    {
      name: "approval_id",
      label: "approval id",
      hidden: true,
      disabled: true,
    },
  ];

  const cosDetails = [
    {
      key: "1",
      label: "Required Details",
      detailItems: [
        {
          name: "name",
          label: "Requester",
          disabled: true,
        },
        {
          name: "start_date",
          label: "Start Date",
          disabled: true,
        },
        {
          name: "end_date",
          label: "End Date",
          disabled: true,
        },
      ],
    },
    {
      key: "2",
      label: "Additional Details",
      detailItems: [
        {
          name: "act1",
          label: "Activity 1",
          disabled: true,
        },
        {
          name: "week1_hours",
          label: "No. of hours per week 1",
          disabled: true,
        },
        {
          name: "act2",
          label: "Activity 2",
          disabled: true,
        },
        {
          name: "week2_hours",
          label: "No. of hours per week 2",
          disabled: true,
        },
        {
          name: "act3",
          label: "Activity 3",
          disabled: true,
        },
        {
          name: "week3_hours",
          label: "No. of hours per week 3",
          disabled: true,
        },
        {
          name: "act4",
          label: "Activity 4",
          disabled: true,
        },
        {
          name: "week4_hours",
          label: "No. of hours per week 4",
          disabled: true,
        },
        {
          name: "act5",
          label: "Activity 5",
          disabled: true,
        },
        {
          name: "week5_hours",
          label: "No. of hours per week 5",
          disabled: true,
        },
      ],
    },
  ];

  const fieldLabel = (text, fieldKey) => (data) =>
    (
      <>
        {text}
        {data?.status === "pending" && data?.edited_fields?.includes(fieldKey) ? (
          <Tag color="gold" style={{ marginLeft: 6 }}>
            Edited
          </Tag>
        ) : null}
      </>
    );

  const viewingDataConfig = [
    {
      key: 1,
      collapseName: "Required Details",
      collapseItems: [
        {
          key: 1,
          name: "start_date",
          label: fieldLabel("Start Date", "start_date"),
        },
        {
          key: 2,
          name: "end_date",
          label: fieldLabel("End Date", "end_date"),
        },
        {
          key: 3,
          name: "absent",
          label: fieldLabel("Date of Absences", "absent"),
        },
      ],
    },
    {
      key: 1,
      collapseName: "Additional Details",
      collapseItems: [
        {
          key: 1,
          name: "act1",
          label: fieldLabel("Activity 1", "act1"),
        },
        {
          key: 2,
          name: "week1_hours",
          label: fieldLabel("No. of hours per week 1", "week1_hours"),
        },
        {
          key: 3,
          name: "act2",
          label: fieldLabel("Activity 2", "act2"),
          hideIfEmpty: true,
        },
        {
          key: 4,
          name: "week2_hours",
          label: fieldLabel("No. of hours per week 2", "week2_hours"),
          hideIfEmpty: true,
        },
        {
          key: 5,
          name: "act3",
          label: fieldLabel("Activity 3", "act3"),
          hideIfEmpty: true,
        },
        {
          key: 6,
          name: "week3_hours",
          label: fieldLabel("No. of hours per week 3", "week3_hours"),
          hideIfEmpty: true,
        },
        {
          key: 7,
          name: "act4",
          label: fieldLabel("Activity 4", "act4"),
          hideIfEmpty: true,
        },
        {
          key: 8,
          name: "week4_hours",
          label: fieldLabel("No. of hours per week 4", "week4_hours"),
          hideIfEmpty: true,
        },
        {
          key: 9,
          name: "act5",
          label: fieldLabel("Activity 5", "act5"),
          hideIfEmpty: true,
        },
        {
          key: 10,
          name: "week5_hours",
          label: fieldLabel("No. of hours per week 5", "week5_hours"),
          hideIfEmpty: true,
        },
      ],
    },
  ];

  return (
    <>
      {loading ? (
        <TableSkeleton tableType="approve" />
      ) : (
        <>
          <Space>
            <p>Approve as: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveApproverPosition}
              popupMatchSelectWidth={false}
            />
          </Space>
          {!hasActiveApproverPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active approver position available.
            </p>
          )}
          <ApproveTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={approverCosPopup}
            popupName="approverCosPopup"
            docuType="Certificate of Service"
            // formFields={cosfields}
            // formDetails={cosDetails}
            viewingDataConfig={viewingDataConfig}
            approverId={employee_id}
            approverposId={selectedPos}
            showDenyRemarksModal
            onRefresh={() => {
              if (selectedPos !== undefined && selectedPos !== null) {
                handleRefresh(selectedPos);
              }
            }}
          />
        </>
      )}
    </>
  );
};

export default ApproveCos;
